import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import './PalletNights.css';

interface WarehouseSummary {
  warehouse_id: string;
  warehouse_name: string;
  pallet_type: string;
  total_pallet_nights: number;
  unique_bins_used: number;
  days_with_data: number;
}

interface DailySnapshot {
  date: string;
  warehouse_name: string;
  pallet_type: string;
  occupied_bins: number;
}

interface MonthlyData {
  yearMonth: string;
  summary: WarehouseSummary[];
  totalPalletNights: number;
  daysWithData: number;
}

const PalletNights: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [monthlyData, setMonthlyData] = useState<MonthlyData | null>(null);
  const [dailyBreakdown, setDailyBreakdown] = useState<DailySnapshot[]>([]);
  const [snapshotDates, setSnapshotDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'summary' | 'calendar'>('summary');

  
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [shiftXCount, setShiftXCount] = useState(0);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  
  
  const [prices, setPrices] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('palletNightPrices');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('palletNightPrices');
      const parsed = saved ? JSON.parse(saved) : {};
      const inputs: Record<string, string> = {};
      for (const key of Object.keys(parsed)) {
        inputs[key] = String(parsed[key]).replace('.', ',');
      }
      return inputs;
    } catch {
      return {};
    }
  });

  
  const [dayActionDate, setDayActionDate] = useState<string | null>(null);
  const [dayDetails, setDayDetails] = useState<any | null>(null);
  const [dayDetailsLoading, setDayDetailsLoading] = useState(false);

  
  const COLUMN_PREFS_KEY = 'palletNightColumnPrefs';

  const getSavedColumnPrefs = (): Record<string, string> => {
    try {
      const saved = localStorage.getItem(COLUMN_PREFS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const saveColumnPrefs = () => {
    const prefs: Record<string, string> = {};
    if (storageNameColumn) prefs.storageNameColumn = storageNameColumn;
    if (warehouseNameColumn) prefs.warehouseNameColumn = warehouseNameColumn;
    if (balanceQuantityColumn) prefs.balanceQuantityColumn = balanceQuantityColumn;
    if (palletTypeColumn) prefs.palletTypeColumn = palletTypeColumn;
    if (itemNameColumn) prefs.itemNameColumn = itemNameColumn;
    if (excludeKeywords.trim()) prefs.excludeKeywords = excludeKeywords.trim();
    localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(prefs));
  };

  
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadDate, setUploadDate] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadColumns, setUploadColumns] = useState<string[]>([]);
  const [storageNameColumn, setStorageNameColumn] = useState('');
  const [warehouseNameColumn, setWarehouseNameColumn] = useState('');
  const [balanceQuantityColumn, setBalanceQuantityColumn] = useState('');
  const [palletTypeColumn, setPalletTypeColumn] = useState('');
  const [itemNameColumn, setItemNameColumn] = useState('');
  const [excludeKeywords, setExcludeKeywords] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMonthlyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [summaryRes, dailyRes, datesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/v1/pallet-nights/summary/${selectedMonth}`),
        fetch(`${API_BASE_URL}/v1/pallet-nights/daily/${selectedMonth}`),
        fetch(`${API_BASE_URL}/v1/pallet-nights/dates/${selectedMonth}`),
      ]);

      if (!summaryRes.ok || !dailyRes.ok || !datesRes.ok) {
        throw new Error('Failed to fetch pallet nights data');
      }

      const summaryData = await summaryRes.json();
      const dailyData = await dailyRes.json();
      const datesData = await datesRes.json();

      setMonthlyData(summaryData);
      setDailyBreakdown(dailyData.dailyBreakdown || []);
      setSnapshotDates(datesData.snapshotDates || []);
    } catch (err: any) {
      setError(err.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchMonthlyData();
  }, [fetchMonthlyData]);

  
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key.toLowerCase() === 'x') {
        setShiftXCount(prev => {
          const newCount = prev + 1;
          if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
          resetTimerRef.current = setTimeout(() => {
            setShiftXCount(0);
          }, 3000);
          if (newCount >= 15) {
            setDeleteEnabled(true);
            setTimeout(() => {
              setDeleteEnabled(false);
              setShiftXCount(0);
            }, 30000);
            return 0;
          }
          return newCount;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  
  const handleDayClick = (date: string, hasData: boolean) => {
    if (deleteEnabled) return;
    if (hasData) {
      setDayActionDate(date);
    } else {
      openUploadModal(date);
    }
  };

  const closeDayAction = () => {
    setDayActionDate(null);
    setDayDetails(null);
  };

  const viewDayDetails = async (date: string) => {
    setDayDetailsLoading(true);
    setDayDetails(null);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/pallet-nights/day/${date}`);
      if (!response.ok) throw new Error('Failed to fetch day details');
      const data = await response.json();
      setDayDetails(data);
    } catch (err: any) {
      setError(err.message || 'Error loading day details');
      closeDayAction();
    } finally {
      setDayDetailsLoading(false);
    }
  };

  const openUploadFromAction = (date: string) => {
    closeDayAction();
    openUploadModal(date);
  };

  
  const openUploadModal = (date: string) => {
    setUploadDate(date);
    setUploadFile(null);
    setUploadColumns([]);
    setStorageNameColumn('');
    setWarehouseNameColumn('');
    setBalanceQuantityColumn('');
    setPalletTypeColumn('');
    setItemNameColumn('');
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadDate(null);
    setUploadFile(null);
    setUploadColumns([]);
    setStorageNameColumn('');
    setWarehouseNameColumn('');
    setBalanceQuantityColumn('');
    setPalletTypeColumn('');
    setItemNameColumn('');
    setUploadProgress(null);
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        if (!base64Data) {
          reject(new Error('Грешка при конвертиране на файла'));
          return;
        }
        resolve(base64Data);
      };
      reader.onerror = () => reject(new Error('Грешка при четене на файла'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!file.name.toLowerCase().match(/\.xlsx?$/)) {
      setError('Моля, изберете Excel файл (.xlsx или .xls)');
      return;
    }

    setUploadFile(file);
    setUploadColumns([]);
    setStorageNameColumn('');
    setWarehouseNameColumn('');
    setBalanceQuantityColumn('');
    setPalletTypeColumn('');
    setItemNameColumn('');

    try {
      setUploadProgress('Четене на колони...');
      const fileData = await convertFileToBase64(file);
      const response = await fetch(`${API_BASE_URL}/v1/pallet-nights/get-columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileData }),
      });

      if (!response.ok) throw new Error('Failed to read file columns');

      const data = await response.json();
      const columns: string[] = data.columns || [];
      setUploadColumns(columns);

      
      const prefs = getSavedColumnPrefs();
      if (prefs.storageNameColumn && columns.includes(prefs.storageNameColumn)) setStorageNameColumn(prefs.storageNameColumn);
      if (prefs.warehouseNameColumn && columns.includes(prefs.warehouseNameColumn)) setWarehouseNameColumn(prefs.warehouseNameColumn);
      if (prefs.balanceQuantityColumn && columns.includes(prefs.balanceQuantityColumn)) setBalanceQuantityColumn(prefs.balanceQuantityColumn);
      if (prefs.palletTypeColumn && columns.includes(prefs.palletTypeColumn)) setPalletTypeColumn(prefs.palletTypeColumn);
      if (prefs.itemNameColumn && columns.includes(prefs.itemNameColumn)) setItemNameColumn(prefs.itemNameColumn);
      if (prefs.excludeKeywords) setExcludeKeywords(prefs.excludeKeywords);

      setUploadProgress(null);
    } catch (err: any) {
      setError(err.message || 'Error reading file');
      setUploadProgress(null);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadDate || !storageNameColumn || !warehouseNameColumn || !balanceQuantityColumn) {
      setError('Моля, попълнете всички полета');
      return;
    }

    setUploadLoading(true);
    setUploadProgress('Обработка на файл...');
    setError(null);

    try {
      const fileData = await convertFileToBase64(uploadFile);
      setUploadProgress('Запазване на данни...');

      const response = await fetch(`${API_BASE_URL}/v1/pallet-nights/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileData,
          date: uploadDate,
          storageNameColumn,
          warehouseNameColumn,
          balanceQuantityColumn,
          palletTypeColumn: palletTypeColumn || undefined,
          itemNameColumn: itemNameColumn || undefined,
          excludeKeywords: excludeKeywords.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process file');
      }

      const data = await response.json();
      const skippedInfo = data.skippedRows > 0 ? ` (${data.skippedRows} реда пропуснати)` : '';
      setSuccessMessage(`Данните за ${uploadDate} бяха запазени: ${data.occupiedBins} заети локации${skippedInfo}`);
      saveColumnPrefs();
      closeUploadModal();
      await fetchMonthlyData();
    } catch (err: any) {
      setError(err.message || 'Error uploading file');
    } finally {
      setUploadLoading(false);
      setUploadProgress(null);
    }
  };

  const clearAllData = async () => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете ВСИЧКИ данни за палетни нощувки?')) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/v1/pallet-nights/clear-all`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to clear data');
      setSuccessMessage('Всички данни бяха изтрити успешно.');
      fetchMonthlyData();
    } catch (err: any) {
      setError(err.message || 'Error clearing data');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (yearMonth: string): number => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  const generateCalendarDays = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = getDaysInMonth(selectedMonth);
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    const days: { date: string | null; hasData: boolean }[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({ date: null, hasData: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
      days.push({ date: dateStr, hasData: snapshotDates.includes(dateStr) });
    }
    return days;
  };

  const getBinsForDate = (date: string): number => {
    return dailyBreakdown.filter(d => d.date === date).reduce((sum, d) => sum + d.occupied_bins, 0);
  };

  const formatMonth = (yearMonth: string): string => {
    const [year, month] = yearMonth.split('-');
    const monthNames = [
      'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни',
      'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const canSubmitUpload = uploadFile && storageNameColumn && warehouseNameColumn && balanceQuantityColumn;

  const deleteDaySnapshot = async (date: string) => {
    const confirmText = window.prompt(
      `За да изтриете данните за ${date}, напишете датата (${date}) по-долу:`
    );
    if (confirmText !== date) {
      if (confirmText !== null) {
        setError('Датата не съвпада. Изтриването е отказано.');
      }
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/v1/pallet-nights/snapshot/${date}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete snapshot');
      setSuccessMessage(`Данните за ${date} бяха изтрити успешно.`);
      await fetchMonthlyData();
    } catch (err: any) {
      setError(err.message || 'Error deleting snapshot');
    } finally {
      setLoading(false);
    }
  };

  const formatBG = (num: number): string => {
    return num.toFixed(2).replace('.', ',');
  };

  const updatePrice = (palletType: string, rawValue: string) => {
    
    const cleaned = rawValue.replace(/[^\d,.\-]/g, '');
    setPriceInputs(prev => ({ ...prev, [palletType]: cleaned }));
    const num = parseFloat(cleaned.replace(',', '.')) || 0;
    const updated = { ...prices, [palletType]: num };
    setPrices(updated);
    localStorage.setItem('palletNightPrices', JSON.stringify(updated));
  };

  const getPrice = (palletType: string): number => prices[palletType] || 0;

  const getTotalCost = (): number => {
    if (!monthlyData) return 0;
    return monthlyData.summary.reduce((sum, row) => {
      return sum + row.total_pallet_nights * getPrice(row.pallet_type);
    }, 0);
  };

  return (
    <div className="pallet-nights">
      {}
      {uploadModalOpen && (
        <div className="snapshot-overlay" onClick={(e) => { if (e.target === e.currentTarget && !uploadLoading) closeUploadModal(); }}>
          <div className="upload-modal">
            {uploadLoading ? (
              <div className="upload-modal-loading">
                <div className="progress-spinner"></div>
                <div className="progress-text">{uploadProgress}</div>
                <div className="progress-subtext">Моля, изчакайте...</div>
              </div>
            ) : (
              <>
                <div className="upload-modal-header">
                  <h3>Качване на данни за {uploadDate}</h3>
                  <button className="modal-close" onClick={closeUploadModal}>×</button>
                </div>

                <div className="upload-modal-body">
                  {}
                  <div className="upload-field">
                    <label>Excel файл (.xlsx)</label>
                    <div
                      className={`file-drop-area ${uploadFile ? 'has-file' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                      {uploadFile ? (
                        <span className="file-name">📄 {uploadFile.name}</span>
                      ) : (
                        <span className="file-placeholder">📂 Кликнете тук или плъзнете файл</span>
                      )}
                    </div>
                  </div>

                  {uploadProgress && !uploadLoading && (
                    <div className="upload-progress-inline">
                      <div className="spinner-small"></div>
                      <span>{uploadProgress}</span>
                    </div>
                  )}

                  {}
                  {uploadColumns.length > 0 && (
                    <div className="column-mapping">
                      <h4>Съпоставяне на колони</h4>

                      <div className="mapping-field">
                        <label><span className="required">*</span> Име на локация</label>
                        <select value={storageNameColumn} onChange={(e) => setStorageNameColumn(e.target.value)}>
                          <option value="">-- Изберете колона --</option>
                          {uploadColumns.map((col) => (
                            <option key={`storage-${col}`} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-field">
                        <label><span className="required">*</span> Име на склад</label>
                        <select value={warehouseNameColumn} onChange={(e) => setWarehouseNameColumn(e.target.value)}>
                          <option value="">-- Изберете колона --</option>
                          {uploadColumns.map((col) => (
                            <option key={`warehouse-${col}`} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-field">
                        <label><span className="required">*</span> Наличност</label>
                        <select value={balanceQuantityColumn} onChange={(e) => setBalanceQuantityColumn(e.target.value)}>
                          <option value="">-- Изберете колона --</option>
                          {uploadColumns.map((col) => (
                            <option key={`quantity-${col}`} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-field">
                        <label>Тип Палет <span className="optional-label">(по избор)</span></label>
                        <select value={palletTypeColumn} onChange={(e) => setPalletTypeColumn(e.target.value)}>
                          <option value="">-- Без групиране по тип --</option>
                          {uploadColumns.map((col) => (
                            <option key={`pallettype-${col}`} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mapping-field">
                        <label>Име на продукт <span className="optional-label">(за изключване)</span></label>
                        <select value={itemNameColumn} onChange={(e) => setItemNameColumn(e.target.value)}>
                          <option value="">-- Без изключване --</option>
                          {uploadColumns.map((col) => (
                            <option key={`itemname-${col}`} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>

                      {itemNameColumn && (
                        <div className="mapping-field">
                          <label>Изключи продукти съдържащи <span className="optional-label">(разделени със запетая)</span></label>
                          <input
                            type="text"
                            className="exclude-input"
                            value={excludeKeywords}
                            onChange={(e) => setExcludeKeywords(e.target.value)}
                            placeholder="напр. палет, кашон, празен"
                          />
                          {excludeKeywords && (
                            <div className="exclude-preview">
                              {excludeKeywords.split(',').map((kw, i) => kw.trim()).filter(Boolean).map((kw, i) => (
                                <span key={i} className="exclude-tag">{kw}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="upload-modal-footer">
                  <button className="cancel-button" onClick={closeUploadModal}>Отказ</button>
                  <button
                    className="submit-upload-button"
                    onClick={handleUploadSubmit}
                    disabled={!canSubmitUpload}
                  >
                    📊 Обработи и Запази
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {}
      {dayActionDate && (
        <div className="snapshot-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeDayAction(); }}>
          <div className="upload-modal day-details-modal">
            <div className="upload-modal-header">
              <h3>Данни за {dayActionDate}</h3>
              <button className="modal-close" onClick={closeDayAction}>×</button>
            </div>

            {!dayDetails && !dayDetailsLoading && (
              <div className="day-action-choices">
                <button className="day-action-button view" onClick={() => viewDayDetails(dayActionDate)}>
                  📊 Виж какво е изчислено
                </button>
                <button className="day-action-button upload" onClick={() => openUploadFromAction(dayActionDate)}>
                  📂 Качи нов файл (презапис)
                </button>
              </div>
            )}

            {dayDetailsLoading && (
              <div className="upload-modal-loading">
                <div className="progress-spinner"></div>
                <div className="progress-text">Зареждане...</div>
              </div>
            )}

            {dayDetails && (
              <div className="day-details-content">
                <div className="day-details-summary">
                  <span className="detail-stat">Общо заети локации: <strong>{dayDetails.totalBins}</strong></span>
                </div>

                {dayDetails.byWarehouse.map((group: any, gIdx: number) => (
                  <div key={gIdx} className="day-details-group">
                    <div className="day-details-group-header">
                      <span className="group-warehouse">{group.warehouse_name}</span>
                      <span className="group-type">{group.pallet_type}</span>
                      <span className="group-count">{group.bins.length} локации</span>
                    </div>
                    <div className="day-details-bins">
                      {group.bins.map((bin: any, bIdx: number) => (
                        <span key={bIdx} className="bin-tag">{bin.bin_name}</span>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="day-details-footer">
                  <button className="cancel-button" onClick={closeDayAction}>Затвори</button>
                  <button className="submit-upload-button" onClick={() => openUploadFromAction(dayActionDate)}>
                    📂 Качи нов файл (презапис)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pallet-nights-header">
        <h2>Pallet Nights / Палетни Нощувки</h2>
        <p className="description">
          Следене на заетите палетни места по дни за месечно таксуване.
          Всяко палетно място с количество {'>'} 0 се брои като 1 палетна нощувка.
        </p>
      </div>

      {}
      <div className="controls-section">
        <div className="month-selector">
          <label>Месец:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </div>

        <button className="refresh-button" onClick={fetchMonthlyData} disabled={loading}>
          🔄 Опресни
        </button>

        {deleteEnabled && (
          <button className="clear-all-button" onClick={clearAllData} disabled={loading}>
            🗑️ Изтрий Всички Данни
          </button>
        )}
      </div>

      {deleteEnabled && (
        <div className="delete-mode-notification">
          🗑️ Режим на изтриване е активен за 30 секунди
        </div>
      )}

      {error && (
        <div className="error-message">
          ❌ {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">
          ✅ {successMessage}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {}
      <div className="view-tabs">
        <button
          className={activeView === 'summary' ? 'active' : ''}
          onClick={() => setActiveView('summary')}
        >
          📊 Обобщение
        </button>
        <button
          className={activeView === 'calendar' ? 'active' : ''}
          onClick={() => setActiveView('calendar')}
        >
          🗓️ Календар
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Зареждане на данни...</p>
        </div>
      ) : (
        <>
          {}
          {activeView === 'summary' && monthlyData && (
            <div className="summary-view">
              <div className="summary-header">
                <h3>{formatMonth(selectedMonth)}</h3>
                <div className="total-stats">
                  <div className="stat-box total">
                    <span className="stat-value">{monthlyData.totalPalletNights}</span>
                    <span className="stat-label">Общо Палетни Нощувки</span>
                  </div>
                  <div className="stat-box days">
                    <span className="stat-value">{monthlyData.daysWithData}</span>
                    <span className="stat-label">Дни с Данни</span>
                  </div>
                  {getTotalCost() > 0 && (
                    <div className="stat-box cost">
                      <span className="stat-value">{formatBG(getTotalCost())}</span>
                      <span className="stat-label">Обща Стойност (лв.)</span>
                    </div>
                  )}
                </div>
              </div>

              {monthlyData.summary.length > 0 ? (
                <table className="summary-table">
                  <thead>
                    <tr>
                      <th>Склад</th>
                      <th>Тип Палет</th>
                      <th>Палетни Нощувки</th>
                      <th>Уникални Локации</th>
                      <th>Дни с Данни</th>
                      <th>Цена/Нощувка</th>
                      <th>Стойност</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.summary.map((row, idx) => {
                      const price = getPrice(row.pallet_type);
                      const cost = row.total_pallet_nights * price;
                      return (
                        <tr key={`${row.warehouse_id}-${row.pallet_type}-${idx}`}>
                          <td>{row.warehouse_name}</td>
                          <td>{row.pallet_type}</td>
                          <td className="number">{row.total_pallet_nights}</td>
                          <td className="number">{row.unique_bins_used}</td>
                          <td className="number">{row.days_with_data}</td>
                          <td className="number price-cell">
                            <input
                              type="text"
                              className="price-input"
                              value={priceInputs[row.pallet_type] ?? ''}
                              onChange={(e) => updatePrice(row.pallet_type, e.target.value)}
                              placeholder="0,00"
                            />
                          </td>
                          <td className="number cost-cell">{cost > 0 ? formatBG(cost) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>ОБЩО</strong></td>
                      <td className="number"><strong>{monthlyData.totalPalletNights}</strong></td>
                      <td className="number">-</td>
                      <td className="number"><strong>{monthlyData.daysWithData}</strong></td>
                      <td className="number">-</td>
                      <td className="number cost-cell"><strong>{getTotalCost() > 0 ? formatBG(getTotalCost()) : '-'}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="no-data">
                  <p>Няма данни за избрания месец.</p>
                  <p>Отидете на "Календар" и кликнете върху ден за да качите файл.</p>
                </div>
              )}
            </div>
          )}

          {activeView === 'calendar' && (
            <div className="calendar-view">
              <h3>Календар - {formatMonth(selectedMonth)}</h3>
              
              <div className="calendar-grid">
                <div className="calendar-header">
                  <span>Нед</span>
                  <span>Пон</span>
                  <span>Вт</span>
                  <span>Ср</span>
                  <span>Чет</span>
                  <span>Пет</span>
                  <span>Съб</span>
                </div>
                <div className="calendar-days">
                  {generateCalendarDays().map((day, index) => (
                    <div
                      key={index}
                      className={`calendar-day ${day.date ? '' : 'empty'} ${day.hasData ? 'has-data' : ''} ${deleteEnabled && day.hasData ? 'delete-mode' : ''}`}
                      onClick={() => day.date && !deleteEnabled && handleDayClick(day.date, day.hasData)}
                      title={day.date ? (deleteEnabled && day.hasData ? 'Кликни 🗑️ за изтриване' : day.hasData ? `${getBinsForDate(day.date)} заети локации (кликни за презаписване)` : 'Кликни за качване на файл') : ''}
                    >
                      {day.date && (
                        <>
                          <span className="day-number">{parseInt(day.date.split('-')[2])}</span>
                          {day.hasData && (
                            <span className="bins-count">{getBinsForDate(day.date)}</span>
                          )}
                          {deleteEnabled && day.hasData && (
                            <button
                              className="day-delete-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteDaySnapshot(day.date!);
                              }}
                              title={`Изтрий данни за ${day.date}`}
                            >
                              🗑️
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="calendar-legend">
                <span className="legend-item">
                  <span className="legend-dot has-data"></span>
                  С данни (кликни за презаписване)
                </span>
                <span className="legend-item">
                  <span className="legend-dot no-data"></span>
                  Без данни (кликни за качване)
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PalletNights;
